import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CalendarClock, CheckCircle2, AlertTriangle, Clock3,
  ChevronDown, ChevronRight,
} from "lucide-react";
import { API } from "@/contexts/AuthContext";

/**
 * FOH dashboard — "MOT day load" mini-tile.
 *
 * Top row: 3 colour-coded stat chips (Imported / Awaiting / Manual review).
 * Click any chip OR the header to expand a clickable list of today's MOT
 * slots — every row links to `/app/tech/mot/{job_id}` so the FOH can land
 * directly on the MOT workspace / jobsheet for any slot (not only the
 * ones that have already imported a result).
 *
 * Auto-refreshes both stats + slot list every 60s.
 */
const TONE = {
  emerald: {
    chip: "bg-emerald-50 border-emerald-200 text-emerald-800",
    row:  "border-emerald-200 bg-emerald-50/50",
    pill: "bg-emerald-100 text-emerald-800",
  },
  slate: {
    chip: "bg-slate-50 border-slate-200 text-slate-700",
    row:  "border-slate-200 bg-white",
    pill: "bg-slate-100 text-slate-700",
  },
  amber: {
    chip: "bg-amber-50 border-amber-200 text-amber-800",
    row:  "border-amber-200 bg-amber-50/40",
    pill: "bg-amber-100 text-amber-900",
  },
};

const STATUS_TONE = {
  PRICING_READY:                    "emerald",
  DVSA_RESULT_IMPORTED:             "emerald",
  COMPLETE:                          "emerald",
  CUSTOMER_CONTACT_REQUIRED:        "emerald",
  MOT_RESULT_PENDING_MANUAL_REVIEW: "amber",
};

const MOTDayLoadTile = () => {
  const [stats, setStats] = useState(null);
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/foh/mot-day-load`),
        axios.get(`${API}/foh/mot-day-slots`),
      ]);
      setStats(s.data);
      setSlots(l.data?.rows || []);
    } catch {
      setStats(null);
      setSlots(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const handle = setInterval(fetchAll, 60_000);
    return () => clearInterval(handle);
  }, [fetchAll]);

  if (loading) return null;
  if (!stats || !stats.total) return null;

  const dateLabel = (() => {
    try {
      return new Date(`${stats.for_date}T00:00:00`).toLocaleDateString(
        undefined,
        { weekday: "short", day: "numeric", month: "short" },
      );
    } catch {
      return stats.for_date;
    }
  })();

  const goTo = (row) => {
    if (row.job_id) navigate(`/app/tech/mot/${row.job_id}`);
  };

  return (
    <div
      className="mb-5 rounded-lg border border-slate-200 bg-white px-4 py-3"
      data-testid="mot-day-load-tile"
    >
      {/* Header — clickable to toggle the slot list */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between mb-2 group"
        data-testid="mot-day-load-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="w-4 h-4 text-slate-500 group-hover:text-slate-700" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-700" />
          )}
          <CalendarClock className="w-4 h-4 text-teal-700" />
          <span className="text-sm font-semibold text-slate-800">
            MOT day load
          </span>
          <span className="text-[11.5px] text-slate-500 italic">
            {dateLabel}
          </span>
        </div>
        <span
          className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-400"
          data-testid="mot-day-load-total"
        >
          {stats.total} slot{stats.total === 1 ? "" : "s"} booked
        </span>
      </button>

      {/* Stat chips — also act as toggle triggers */}
      <div className="grid grid-cols-3 gap-2">
        <Chip label="Imported" value={stats.imported} icon={CheckCircle2}
              tone="emerald" testid="mot-day-load-imported"
              onClick={() => setOpen(true)} />
        <Chip label="Awaiting" value={stats.awaiting} icon={Clock3}
              tone="slate" testid="mot-day-load-awaiting"
              onClick={() => setOpen(true)} />
        <Chip label="Manual review" value={stats.manual_review}
              icon={AlertTriangle} tone="amber"
              testid="mot-day-load-manual" onClick={() => setOpen(true)} />
      </div>

      {/* Slot list — clickable to jump straight to the MOT workspace */}
      {open && (
        <div
          className="mt-3 space-y-1.5"
          data-testid="mot-day-load-slot-list"
        >
          {(slots || []).length === 0 && (
            <p className="text-[12px] italic text-slate-400 px-1 py-1">
              No MOT slots loaded.
            </p>
          )}
          {(slots || []).map((row) => {
            const tone = TONE[STATUS_TONE[row.workflow_status] || "slate"];
            const timeLabel = (() => {
              if (!row.mot_slot_time) return "";
              try {
                return new Date(row.mot_slot_time).toLocaleTimeString(
                  undefined, { hour: "2-digit", minute: "2-digit" },
                );
              } catch {
                return row.mot_slot_time;
              }
            })();
            const clickable = !!row.job_id;
            return (
              <button
                key={row.session_id}
                type="button"
                disabled={!clickable}
                onClick={() => goTo(row)}
                className={`w-full text-left rounded-md border px-3 py-2 flex items-center gap-3 transition ${
                  tone.row
                } ${
                  clickable
                    ? "cursor-pointer hover:shadow-sm hover:border-slate-300"
                    : "cursor-not-allowed opacity-60"
                }`}
                data-testid={`mot-day-load-row-${row.session_id}`}
                title={clickable ? "Open MOT workflow" : "Job not yet linked"}
              >
                <span className="text-[12.5px] font-semibold text-slate-700 w-12 shrink-0">
                  {timeLabel}
                </span>
                <span className="text-[13px] font-bold text-slate-900 min-w-[6rem]">
                  {row.vrm}
                </span>
                {row.customer_name && (
                  <span className="text-[12px] text-slate-600 truncate">
                    {row.customer_name}
                  </span>
                )}
                <span className="flex-1" />
                {row.test_result && (
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                      row.test_result === "PASSED"
                        ? "bg-emerald-200 text-emerald-900"
                        : "bg-red-200 text-red-900"
                    }`}
                  >
                    {row.test_result}
                  </span>
                )}
                {row.defect_terminal > 0 && (
                  <span className="text-[10.5px] text-red-700 font-semibold">
                    {row.defect_terminal} fail
                  </span>
                )}
                {row.defect_advisory > 0 && (
                  <span className="text-[10.5px] text-amber-700">
                    {row.defect_advisory} adv
                  </span>
                )}
                <span
                  className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${tone.pill}`}
                >
                  {row.status_label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Chip = ({ label, value, icon: Icon, tone, testid, onClick }) => {
  const dim = value === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2.5 py-2 flex items-center gap-2 text-left transition hover:shadow-sm ${
        TONE[tone].chip
      } ${dim ? "opacity-50" : ""}`}
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.12em] font-semibold leading-tight">
          {label}
        </div>
        <div className="text-[16px] font-bold leading-tight">{value}</div>
      </div>
    </button>
  );
};

export default MOTDayLoadTile;
